import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { problemCode } from '../../api/problemCode';
import { useGetJobQuery } from '../../job/jobEndpoints';
import { useListTagsQuery } from '../../tag/tagEndpoints';
import { Icon } from '../../common/Icon';
import {
  DifficultyMeter,
  LanguageCode,
  RelativeTime,
  SourceHint,
  TagChips,
  TaskTypeBadge,
} from '../TaskBadges';
import { TaskFilters } from '../TaskFilters';
import { TaskOutcomeNotice } from '../TaskOutcomeNotice';
import { TaskListFilters, useListTasksInfiniteQuery } from '../taskEndpoints';
import { readFilters, readTaskNavState, TaskNavState, writeFilters } from '../taskListFilters';

/** The longest a job's request is shown as the filter chip's title before being cut (QZ-D59). */
const JOB_REQUEST_MAX = 40;

/** QZ-D8, QZ-D15, QZ-D16: the task list at `/tasks`, filtered and paged through the URL. */
export const TaskListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { notice } = readTaskNavState(location.state);

  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, error } =
    useListTasksInfiniteQuery(filters);
  const { data: tagList } = useListTagsQuery();
  // `currentData`, not `data`: while a new jobId's job loads, the previous job's title must
  // not flash before the chip catches up (QZ-D59).
  const { currentData: job } = useGetJobQuery(filters.jobId ?? skipToken);

  const tasks = data?.pages.flatMap((page) => page.tasks) ?? [];
  const code = problemCode(error);
  const changeFilters = (next: TaskListFilters) => setSearchParams(writeFilters(next));

  // The task page takes its next draft from this same list.
  const open = (taskId: string) =>
    navigate(`/tasks/${taskId}`, { state: { from: filters } satisfies TaskNavState });

  const request = job?.request ?? '';
  const jobTitle = job
    ? (job.title ??
      (request.length <= JOB_REQUEST_MAX ? request : `${request.slice(0, JOB_REQUEST_MAX)}…`))
    : undefined;

  return (
    <main className="qz-main">
      <div className="qz-container">
        <div className="qz-page-header">
          <div>
            <h1>{t('task.list.heading')}</h1>
            <p className="qz-sub">{t(`task.list.sub.${filters.status}`)}</p>
          </div>
          <Link to="/jobs/new" className="btn btn-primary">
            <Icon name="wand-magic-sparkles" /> {t('task.list.newJob')}
          </Link>
        </div>

        <TaskOutcomeNotice key={location.key} notice={notice} />

        <div className="qz-card">
          <TaskFilters
            filters={filters}
            tags={tagList?.tags ?? []}
            jobTitle={jobTitle}
            onChange={changeFilters}
          />

          {isLoading && <p className="qz-card-body qz-muted mb-0">{t('task.list.loading')}</p>}
          {error && (
            <div className="qz-card-body">
              <div className="alert alert-danger mb-0">
                <Icon name="circle-exclamation" />
                <div>{t(code ? `error.${code}` : 'error.unknown')}</div>
              </div>
            </div>
          )}
          {!isLoading && !error && tasks.length === 0 && !hasNextPage && (
            <div className="qz-empty">
              <Icon name="inbox" />
              <strong>{t('task.list.empty')}</strong>
              <button
                type="button"
                className="btn btn-link btn-sm"
                onClick={() => changeFilters({ status: filters.status, jobId: filters.jobId })}
              >
                {t('task.list.clearFilters')}
              </button>
            </div>
          )}
          {!isLoading && tasks.length > 0 && (
            <table className="table table-hover table-clickable align-middle">
              <thead>
                <tr>
                  <th style={{ width: '44%' }}>{t('task.field.question')}</th>
                  <th>{t('task.field.taskTypes')}</th>
                  <th>{t('task.field.tags')}</th>
                  <th>{t('task.field.difficulty')}</th>
                  <th>{t('task.field.languageShort')}</th>
                  <th className="text-end">{t('task.field.updatedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => {
                  const text = task.translations[task.originalLanguage]?.text ?? '';
                  const truncated = text.length <= 80 ? text : `${text.slice(0, 80)}…`;
                  return (
                    <tr
                      key={task.taskId}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(task.taskId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          open(task.taskId);
                        } else if (e.key === ' ') {
                          e.preventDefault();
                          open(task.taskId);
                        }
                      }}
                    >
                      <td>
                        <div className="fw-semibold">{truncated}</div>
                        {task.status === 'rejected' ? (
                          <div className="qz-small text-danger">
                            <Icon name="circle-xmark" />{' '}
                            {task.rejectionReason ?? t('task.list.noReason')}
                          </div>
                        ) : (
                          <SourceHint source={task.sourceReference} />
                        )}
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {task.taskTypes.map((taskType) => (
                            <TaskTypeBadge key={taskType} type={taskType} />
                          ))}
                        </div>
                      </td>
                      <td>
                        <TagChips tags={task.tags} />
                      </td>
                      <td>
                        <DifficultyMeter difficulty={task.difficulty} />
                      </td>
                      <td>
                        <LanguageCode language={task.originalLanguage} />
                      </td>
                      <td className="text-end qz-small qz-muted">
                        <RelativeTime iso={task.updatedAt} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!isLoading && tasks.length > 0 && (
            <div className="qz-card-footer d-flex align-items-center">
              <span className="qz-small qz-muted">
                {t('task.list.showing', { count: tasks.length })}
              </span>
              {hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm ms-auto"
                  disabled={isFetching}
                  onClick={() => fetchNextPage()}
                >
                  {t('task.list.loadMore')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default TaskListPage;
